import React, { useState, useEffect } from 'react';
import ReactDropZone from "react-dropzone";
import { CircularProgress, IconButton, Tooltip, Typography, Button, InputLabel } from "@material-ui/core";
import { CloudUpload, Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import axios from "axios";
import EnhancedTextField from './EnhancedTextField';
import DocumentViewer from './DocumentViewer';
import EnhancedErrorDialog from "./EnhancedErrorDialog";

const EnhancedDocumentField = (props) => {
  const [error, setError] = useState(props.error || '');
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [uploadError, setUploadError] = useState({ open: false, errorHeader: "", errorMessage: "" });
  const [inputMode, setInputMode] = useState((props.uploadUrl || props.Config?.api) ? 'upload' : 'url'); // 'upload' or 'url'

  // Supported file extensions
  const supportedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.rtf'];
  
  const validateDocumentUrl = (url) => {
    if (!url) {
      setError('');
      setShowPreview(false);
      return true;
    }
    
    // Valid URL patterns for document services
    const googleDrivePattern = /^https:\/\/drive\.google\.com\/(file\/d\/|open\?id=)/;
    const dropboxPattern = /^https:\/\/(www\.)?dropbox\.com\/(s|sh)\//;
    const oneDrivePattern = /^https:\/\/1drv\.ms\//;
    
    // Check if it's a valid URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      
      // Check for direct document links
      const hasValidExtension = supportedExtensions.some(ext => pathname.endsWith(ext));
      
      // Check for document service links
      const isGoogleDrive = googleDrivePattern.test(url);
      const isDropbox = dropboxPattern.test(url);
      const isOneDrive = oneDrivePattern.test(url);
      
      if (hasValidExtension || isGoogleDrive || isDropbox || isOneDrive) {
        setError('');
        setShowPreview(true);
        return true;
      } else {
        setError('Please enter a valid document URL (PDF, DOC, DOCX, XLS, XLSX, CSV, TXT, RTF) or a link from Google Drive, Dropbox, or OneDrive');
        setShowPreview(false);
        return false;
      }
    } catch (e) {
      setError('Please enter a valid URL');
      setShowPreview(false);
      return false;
    }
  };

  const uploadFile = async (files) => {
    if (!files || !files.length)
      return;

    setLoadingDocument(true);
    let formData = new FormData();
    formData.append("file", files[0]);
    
    // Get file extension
    const fileName = files[0].name;
    const fileExtension = fileName.split('.').pop().toLowerCase();

    try {
      // Construct upload URL with proper parameters (matching ProductImages.js logic)
      let uploadUrl;
      if (props.uploadUrl) {
        uploadUrl = props.uploadUrl;
        // Add file type parameter to custom upload URL if provided
        if (fileExtension) {
          const separator = uploadUrl.includes('?') ? '&' : '?';
          uploadUrl += `${separator}type=${fileExtension}`;
        }
      } else if (props.Config?.api) {
        uploadUrl = props.Config.api + `/api/v2/ProductOnboardingCenter/documents`;
        
        // Add partnerId parameter based on catalog type (matching ProductImages.js logic exactly)
        let params = [];
        if (props.isRetailer) {
          if (props.catalog !== "mycatalog") {
            params.push(`partnerId=${props.partnerId}`);
          }
        } else {
          params.push(`partnerId=${props.partnerId}`);
        }
        
        // Add file type parameter
        if (fileExtension) {
          params.push(`type=${fileExtension}`);
        }
        
        if (params.length > 0) {
          uploadUrl += '?' + params.join('&');
        }
        
      } else {
        throw new Error("No upload URL configured");
      }

      let result = (await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })).data;

      let link = result.Body;
      if (props.onChange) {
        // Create a synthetic event for consistency with onChange handlers
        const syntheticEvent = {
          target: { value: link },
          preventDefault: () => {},
          stopPropagation: () => {}
        };
        props.onChange(syntheticEvent);
      }
      setLoadingDocument(false);
      setUploadError({ open: false, errorHeader: "", errorMessage: "" });
      setShowPreview(true);
    } catch (e) {
      console.error('Document upload error:', e.response || e);
      setLoadingDocument(false);
      setUploadError({ 
        open: true, 
        errorHeader: "Error uploading document!", 
        errorMessage: e.response?.data?.message || e.message 
      });
    }
  };

  useEffect(() => {
    if (props.value) {
      validateDocumentUrl(props.value);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    validateDocumentUrl(newValue);
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setShowPreview(false);
  };

  const handleBlur = () => {
    if (props.value) {
      validateDocumentUrl(props.value);
    }
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (props.onChange) {
      const syntheticEvent = {
        target: { value: undefined },
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      props.onChange(syntheticEvent);
    }
    setShowPreview(false);
    setIsEditing(false);
  };

  // File type mapping for dropzone accept prop
  const acceptedFileTypes = supportedExtensions.map(ext => {
    const mimeTypes = {
      '.pdf': 'application/pdf',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls': 'application/vnd.ms-excel',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.csv': 'text/csv',
      '.txt': 'text/plain',
      '.rtf': 'application/rtf'
    };
    return mimeTypes[ext];
  }).filter(Boolean).join(', ');

  if (props.template)
    return props.template(uploadFile);

  return (
    <div style={{ width: "100%", ...props.style }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <InputLabel style={{ 
          position: 'static',
          transform: 'none',
          textAlign: 'left',
          ...props.labelstyles
        }}>
          {props.label}
        </InputLabel>
        {!props.disabled && (props.value || isEditing) && (
          <div style={{
            display: 'flex',
            gap: '4px'
          }}>
            {isEditing ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleBlur}
                style={{ minWidth: '60px' }}
              >
                Done
              </Button>
            ) : (
              <>
                <Tooltip title='Edit'>
                  <IconButton 
                    size="small"
                    onClick={handleEdit}
                    style={{ padding: '4px' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Delete'>
                  <IconButton 
                    size="small"
                    onClick={handleDelete}
                    style={{ padding: '4px' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        )}
      </div>
      {(isEditing || !showPreview || !props.value) ? (
        <>
          {(props.uploadUrl || props.Config?.api) && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <Button
                variant={inputMode === 'upload' ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                onClick={() => setInputMode('upload')}
                disabled={props.disabled}
              >
                Upload File
              </Button>
              <Button
                variant={inputMode === 'url' ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                onClick={() => setInputMode('url')}
                disabled={props.disabled}
              >
                Enter URL
              </Button>
            </div>
          )}

          {inputMode === 'upload' && (props.uploadUrl || props.Config?.api) ? (
            <ReactDropZone 
              onDrop={uploadFile} 
              accept={acceptedFileTypes}
              disabled={props.disabled || loadingDocument}
              style={{
                width: "100%",
                height: "200px",
                border: "2px dashed #ccc",
                borderRadius: "4px",
                cursor: props.disabled ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
                position: "relative"
              }}>
              {({ isDragAccept, isDragReject }) => {
                return (
                  <>
                    {(isDragAccept || isDragReject) && (
                      <div style={{
                        position: 'absolute',
                        height: '100%',
                        paddingLeft: '1em',
                        paddingRight: '1em',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography>Drop here to upload this file.</Typography>
                      </div>
                    )}

                    {loadingDocument && (
                      <div style={{
                        position: 'absolute',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'center'
                      }}>
                        <CircularProgress />
                      </div>
                    )}

                    {!loadingDocument && !isDragAccept && (
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%"
                      }}>
                        <CloudUpload style={{ width: "50px", height: "50px", color: "#ccc", marginBottom: "10px" }} />
                        <Typography style={{ color: "#666" }}>
                          Drag & Drop documents here or click to browse
                        </Typography>
                        <Typography variant="caption" style={{ color: "#999", marginTop: "5px" }}>
                          Supported: PDF, DOC, DOCX, XLS, XLSX, CSV, TXT, RTF
                        </Typography>
                      </div>
                    )}
                  </>
                );
              }}
            </ReactDropZone>
          ) : (
            <EnhancedTextField
              {...props}
              style={{ width: '100%', marginTop: props.uploadUrl ? '10px' : '0' }}
              error={error || props.error}
              helperText={error || props.helperText || 'Enter a document URL (PDF, DOC, XLSX, etc.) or link from Google Drive, Dropbox, OneDrive. Note: External URLs are not saved to our system.'}
              onChange={handleChange}
              placeholder="https://example.com/document.pdf or Google Drive/Dropbox/OneDrive link"
              autoFocus={isEditing}
              value={props.value || ''}
              label={undefined}
            />
          )}
        </>
      ) : null}

      {showPreview && props.value && !isEditing && (
        <DocumentViewer url={props.value} />
      )}

      <EnhancedErrorDialog 
        open={uploadError.open} 
        onClose={() => setUploadError({ ...uploadError, open: false })}
        errorHeader={uploadError.errorHeader} 
        errorMessage={uploadError.errorMessage} 
      />
    </div>
  );
};

export default EnhancedDocumentField;